import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useMe } from '../api/hooks';
import { api, fijarCsrf, navegador } from '../api/client';
import type { Me, Rol } from '../types/api';

const NIVEL: Record<Rol, number> = { 'dwh.lector': 1, 'dwh.editor': 2, 'dwh.admin': 3 };

export interface Sesion {
  me: Me;
  /** Los roles son acumulativos: dwh.admin ⊃ dwh.editor ⊃ dwh.lector. */
  puede: (rol: Rol) => boolean;
  salir: () => Promise<void>;
}

const Contexto = createContext<Sesion | null>(null);

export function crearSesion(me: Me): Sesion {
  const nivel = Math.max(0, ...me.roles.map((r) => NIVEL[r] ?? 0));
  return {
    me,
    puede: (rol) => nivel >= NIVEL[rol],
    salir: async () => {
      const r = await api.post<{ redirect: string }>('/auth/logout');
      navegador.ir(r.redirect);
    },
  };
}

/** Carga GET /api/me (si no hay sesión, el cliente manda al login) y fija el token CSRF. */
export function SesionProvider({ children, alCargar }: { children: (s: Sesion) => ReactNode; alCargar?: ReactNode }) {
  const { data, error, isLoading } = useMe();
  useEffect(() => {
    fijarCsrf(data?.csrf ?? null);
  }, [data?.csrf]);

  if (isLoading || (!data && !error)) return <>{alCargar ?? <p className="cargando">Cargando…</p>}</>;
  if (error || !data) return <p className="aviso aviso-error">No se ha podido comprobar la sesión. Recarga la página.</p>;
  const sesion = crearSesion(data);
  return <Contexto.Provider value={sesion}>{children(sesion)}</Contexto.Provider>;
}

export function ProveedorSesionFijo({ sesion, children }: { sesion: Sesion; children: ReactNode }) {
  return <Contexto.Provider value={sesion}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const s = useContext(Contexto);
  if (!s) throw new Error('useSesion fuera de SesionProvider');
  return s;
}
